import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Tooltip,
  Badge,
} from '@mui/material';
import api from '../services/api';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  VpnKey as LicenseIcon,
  People as PeopleIcon,
  CloudDownload as DownloadIcon,
  Settings as SettingsIcon,
  AccountCircle,
  ExitToApp as LogoutIcon,
  Receipt as CustomerIcon,
  Storage as StorageIcon,
  Api as ApiIcon,
  TableChart as TableIcon,
  Computer as ComputerIcon,
  Backup as BackupIcon,
  ListAlt as LogsIcon,
  Speed as SpeedIcon,
  Sync as SyncIcon,
  MonitorHeart as SystemIcon,
  Notifications as NotificationsIcon,
  SystemUpdate as SystemUpdateIcon,
  HeadsetMic as HeadsetMicIcon,
  Security as RolesIcon,
  EventAvailable as HolidayIcon,
  Contacts as ContactsLinkIcon,
  Hub as HubIcon,
  Payments as PaymentsIcon,
  Storefront as StorefrontIcon,
} from "@mui/icons-material";
import { useAuth } from '../context/AuthContext';
import { useUserActions } from '../hooks/useUserActions';

const DRAWER_OPEN_WIDTH = 240;
const DRAWER_COLLAPSED_WIDTH = 64;

function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(() => {
    try { return localStorage.getItem('sidebarOpen') === 'true'; } catch { return false; }
  });
  const drawerWidth = desktopOpen ? DRAWER_OPEN_WIDTH : DRAWER_COLLAPSED_WIDTH;
  const toggleDesktopDrawer = () => {
    setDesktopOpen((v) => {
      const nv = !v;
      try { localStorage.setItem('sidebarOpen', String(nv)); } catch {}
      return nv;
    });
  };
  const [anchorEl, setAnchorEl] = useState(null);
  const [supportOpenCount, setSupportOpenCount] = useState(0);
  const { user, logout } = useAuth();
  const { hasAction, loading: actionsLoading } = useUserActions();
  const navigate = useNavigate();
  const location = useLocation();

  // Senha do dia GLOBAL — visivel no header em /atendimento e /monitor/clients
  // pra atendente copiar e usar no Tray do cliente. Aceita em qualquer Tray
  // 2.4.65+. Refresh: 1x ao montar + na meia-noite (location change basta).
  const [dailyPassword, setDailyPassword] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null); // {at: Date} publicado por paginas via window CustomEvent
  useEffect(() => {
    const handler = (e) => setLiveStatus(e.detail);
    window.addEventListener('live-status', handler);
    return () => window.removeEventListener('live-status', handler);
  }, []);
  useEffect(() => {
    const showPaths = ['/atendimento', '/monitor/clients'];
    if (!showPaths.includes(location.pathname)) return;
    let cancelled = false;
    api.get('/replicador/daily-password').then(res => {
      if (!cancelled) setDailyPassword(res.data?.dailyPassword || null);
    }).catch(() => { /* silencioso — chip nao aparece se falhar */ });
    return () => { cancelled = true; };
  }, [location.pathname]);

  // Polling do badge de pedidos de suporte abertos. 30s e suficiente —
  // pedido nao e tempo-real critico, mas precisa aparecer rapido.
  useEffect(() => {
    let alive = true;
    const fetch = async () => {
      try {
        const r = await api.get('/api/admin/support-requests/open-count');
        if (alive) setSupportOpenCount(r.data?.count || 0);
      } catch { /* silencia, badge fica em 0 */ }
    };
    fetch();
    const id = setInterval(fetch, 30_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Check if user is superadmin (has all permissions)
  const isSuperAdmin = user?.role === 'superadmin' || user?.role === 'admin';

  // Menu top — ordem: Dashboard / Atendimento / Downloads / Notas-Boletos /
  // Portal-Usuarios / Usuarios / Configurações.
  // Licencas, Atualizador TEF e Agendamento Sync moveram pra Monitoramento
  // (Sync alem disso virou aba dentro de Configurações).
  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', action: 'dashboard.view', permission: 'dashboard' },
    { text: 'Leads', icon: <PeopleIcon />, path: '/leads', adminOnly: true },
    { text: 'Atendimento', icon: <HeadsetMicIcon />, path: '/atendimento', action: 'atendimento.view', permission: 'atendimento' },
    { text: 'Downloads', icon: <DownloadIcon />, path: '/downloads', action: 'downloads.list', permission: 'downloads' },
    { text: 'Notas/Boletos', icon: <CustomerIcon />, path: '/customers', action: 'customers.list', permission: 'customers' },
    { text: 'Portal - Usuarios', icon: <PeopleIcon />, path: '/portal-users', action: 'portal_users.list', permission: 'portalUsers' },
    { text: 'Usuarios', icon: <PeopleIcon />, path: '/users', action: 'users.list', permission: 'users' },
    { text: 'Cargos & Permissões', icon: <RolesIcon />, path: '/roles', action: 'users.permissions.update', permission: 'users', adminOnly: true },
    { text: 'Atendimento e Plantões', icon: <HolidayIcon />, path: '/atendimento-plantoes', action: 'settings.update', permission: 'settings', adminOnly: true },
    { text: 'Feriados', icon: <HolidayIcon />, path: '/holidays', action: 'settings.update', permission: 'settings', adminOnly: true },
    { text: 'Vinculos Contatos', icon: <ContactsLinkIcon />, path: '/contact-links', action: 'atendimento.contact_link.scan', permission: 'settings', adminOnly: true },
    { text: 'Configuracoes', icon: <SettingsIcon />, path: '/settings', action: 'settings.view', permission: 'settings' },
  ];

  // Monitor menu items (ex-Replicador) — agora inclui Licencas e Atualizador TEF
  const monitorMenuItems = [
    { text: 'Visao Geral', icon: <SpeedIcon />, path: '/monitor/dashboard', action: 'monitor.dashboard.view', permission: 'monitor' },
    { text: 'Clientes', icon: <ComputerIcon />, path: '/monitor/clients', action: 'monitor.clients.view', permission: 'monitor' },
    { text: 'Licencas', icon: <LicenseIcon />, path: '/licenses', action: 'license.list', permission: 'licenses' },
    { text: 'Backups', icon: <BackupIcon />, path: '/monitor/backups', action: 'monitor.backups.view', permission: 'monitor' },
    { text: 'Endpoints', icon: <ApiIcon />, path: '/monitor/endpoints', action: 'monitor.endpoints.view', permission: 'monitor' },
    { text: 'Tabelas', icon: <TableIcon />, path: '/monitor/sync-tables', action: 'monitor.tables.view', permission: 'monitor' },
    { text: 'Sistema', icon: <SystemIcon />, path: '/monitor/system', action: 'monitor.system.view', permission: 'monitor' },
    { text: 'Certificados', icon: <LicenseIcon />, path: '/monitor/certificates', action: 'monitor.certificates.view', permission: 'monitor' },
    { text: 'Atualizador TEF', icon: <SystemUpdateIcon />, path: '/tef-updates', action: 'tef_updates.view', permission: 'tefUpdates' },
    { text: 'Alertas', icon: <SystemIcon />, path: '/monitor/alerts', action: 'monitor.alerts.view', permission: 'monitor' },
    { text: 'Logs', icon: <LogsIcon />, path: '/monitor/logs', action: 'monitor.logs.view', permission: 'monitor' },
  ];

  // App Android (OtimizAPP) — licencas app, solicitacoes de extensao, versoes do APK
  const appMenuItems = [
    { text: 'Licencas App', icon: <LicenseIcon />, path: '/licenses?tab=1', action: 'license.list', permission: 'licenses' },
    { text: 'Solicitacoes', icon: <SystemIcon />, path: '/licenses?tab=2', action: 'license.list', permission: 'licenses' },
    { text: 'Versoes APK', icon: <SystemUpdateIcon />, path: '/app-versions', adminOnly: true },
  ];

  // Inovafarma — ferramentas auxiliares pra clientes que rodam Inovafarma ERP.
  const inovafarmaMenuItems = [
    { text: 'Etiquetas', icon: <LicenseIcon />, path: '/etiquetas-inovafarma', action: 'license.update', permission: 'licenses' },
  ];

  // Hub OtimizAPP — gerencia tenants/planos/assinaturas do marketplace de canais.
  // Marketplace publico vive num subdominio separado, nao entra no menu admin.
  // Hub OtimizAPP — visível em admin.otimizapp.com.br (filtrado por isHubOnlyHost)
  const hubMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/hub/dashboard', adminOnly: true },
    { text: 'Licencas', icon: <LicenseIcon />, path: '/hub/subscriptions', adminOnly: true },
    { text: 'Planos', icon: <StorefrontIcon />, path: '/hub/plans-admin', adminOnly: true },
    { text: 'Clientes Marketplace', icon: <PeopleIcon />, path: '/marketplace/users', adminOnly: true },
    { text: 'Pagamentos', icon: <PaymentsIcon />, path: '/hub/payments', adminOnly: true },
    { text: 'Integração Hub', icon: <HubIcon />, path: '/hub/integration', adminOnly: true },
  ];

    // Filter helper: prioriza RBAC actions (granular) com fallback permission (legacy).
  // Durante carregamento de actions, usa permission pra evitar flicker.
  const itemAllowed = (item) => {
    if (isSuperAdmin) return true;
    if (item.adminOnly && user?.role !== 'admin' && user?.role !== 'superadmin') return false;
    if (item.action && !actionsLoading) return hasAction(item.action);
    return user?.permissions?.[item.permission] === true;
  };

  // Quando rodando em admin.otimizapp.com.br, esconde menus do portal Otimize
  // (Replicador/clientes/atendimento) e mostra apenas a seção Hub.
  const isHubOnlyHost = typeof window !== 'undefined' &&
    window.location.hostname.startsWith('admin.otimizapp.');

  const filteredMonitorItems = isHubOnlyHost ? [] : monitorMenuItems.filter(itemAllowed);
  const filteredHubItems = isHubOnlyHost ? hubMenuItems.filter(itemAllowed) : [];
  const filteredAppItems = isHubOnlyHost ? [] : appMenuItems.filter(itemAllowed);
  const filteredInovafarmaItems = isHubOnlyHost ? [] : inovafarmaMenuItems.filter(itemAllowed);
  const filteredMenuItems = isHubOnlyHost ? [] : menuItems.filter(itemAllowed);

  const drawer = (
    <div>
      <Toolbar sx={{ minHeight: 56 }} />
      <Divider />
      <List>
        {filteredMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <Tooltip title={desktopOpen ? '' : item.text} placement="right">
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                sx={{ minHeight: 44, justifyContent: desktopOpen ? 'initial' : 'center', px: 2.5 }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: desktopOpen ? 2 : 'auto', justifyContent: 'center' }}>
                  {item.icon}
                </ListItemIcon>
                {desktopOpen && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
      {filteredMonitorItems.length > 0 && (
        <>
          <Divider />
          <List>
            {desktopOpen && (
              <ListItem>
                <ListItemText
                  primary="Monitoramento"
                  primaryTypographyProps={{
                    variant: 'caption',
                    color: 'textSecondary',
                    fontWeight: 'bold',
                    textTransform: 'uppercase'
                  }}
                />
              </ListItem>
            )}
            {filteredMonitorItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <Tooltip title={desktopOpen ? '' : item.text} placement="right">
                  <ListItemButton
                    selected={location.pathname === item.path}
                    onClick={() => navigate(item.path)}
                    sx={{ minHeight: 44, justifyContent: desktopOpen ? 'initial' : 'center', px: 2.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, mr: desktopOpen ? 2 : 'auto', justifyContent: 'center' }}>
                      {item.icon}
                    </ListItemIcon>
                    {desktopOpen && <ListItemText primary={item.text} />}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        </>
      )}
      {filteredAppItems.length > 0 && (
        <>
          <Divider />
          <List>
            {desktopOpen && (
              <ListItem>
                <ListItemText
                  primary="App Android"
                  primaryTypographyProps={{
                    variant: 'caption',
                    color: 'textSecondary',
                    fontWeight: 'bold',
                    textTransform: 'uppercase'
                  }}
                />
              </ListItem>
            )}
            {filteredAppItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <Tooltip title={desktopOpen ? '' : item.text} placement="right">
                  <ListItemButton
                    selected={location.pathname + location.search === item.path || location.pathname === item.path}
                    onClick={() => navigate(item.path)}
                    sx={{ minHeight: 44, justifyContent: desktopOpen ? 'initial' : 'center', px: 2.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, mr: desktopOpen ? 2 : 'auto', justifyContent: 'center' }}>
                      {item.icon}
                    </ListItemIcon>
                    {desktopOpen && <ListItemText primary={item.text} />}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        </>
      )}
      {filteredInovafarmaItems.length > 0 && (
        <>
          <Divider />
          <List>
            {desktopOpen && (
              <ListItem>
                <ListItemText
                  primary="Inovafarma"
                  primaryTypographyProps={{
                    variant: 'caption',
                    color: 'textSecondary',
                    fontWeight: 'bold',
                    textTransform: 'uppercase'
                  }}
                />
              </ListItem>
            )}
            {filteredInovafarmaItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <Tooltip title={desktopOpen ? '' : item.text} placement="right">
                  <ListItemButton
                    selected={location.pathname + location.search === item.path || location.pathname === item.path}
                    onClick={() => navigate(item.path)}
                    sx={{ minHeight: 44, justifyContent: desktopOpen ? 'initial' : 'center', px: 2.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, mr: desktopOpen ? 2 : 'auto', justifyContent: 'center' }}>
                      {item.icon}
                    </ListItemIcon>
                    {desktopOpen && <ListItemText primary={item.text} />}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        </>
      )}
      {filteredHubItems.length > 0 && (
        <>
          <Divider />
          <List>
            {desktopOpen && (
              <ListItem>
                <ListItemText
                  primary="Hubs OtimizAPP"
                  primaryTypographyProps={{
                    variant: 'caption',
                    color: 'textSecondary',
                    fontWeight: 'bold',
                    textTransform: 'uppercase'
                  }}
                />
              </ListItem>
            )}
            {filteredHubItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <Tooltip title={desktopOpen ? '' : item.text} placement="right">
                  <ListItemButton
                    selected={location.pathname === item.path}
                    onClick={() => navigate(item.path)}
                    sx={{ minHeight: 44, justifyContent: desktopOpen ? 'initial' : 'center', px: 2.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, mr: desktopOpen ? 2 : 'auto', justifyContent: 'center' }}>
                      {item.icon}
                    </ListItemIcon>
                    {desktopOpen && <ListItemText primary={item.text} />}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={toggleDesktopDrawer}
            sx={{ mr: 2, display: { xs: 'none', sm: 'inline-flex' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold', letterSpacing: 0.3 }}>
              Portal Otimize Informática
            </Typography>
            <Typography variant="body2" noWrap sx={{ opacity: 0.85, display: { xs: 'none', sm: 'block' } }}>
              {filteredMenuItems.find(item => item.path === location.pathname)?.text ||
               filteredMonitorItems.find(item => item.path === location.pathname)?.text ||
               filteredInovafarmaItems.find(item => item.path === location.pathname)?.text ||
               ''}
            </Typography>
          </Box>
          
          <Tooltip title={supportOpenCount > 0 ? `${supportOpenCount} pedido(s) de suporte aberto(s)` : 'Pedidos de suporte'}>
            <IconButton
              size="large"
              color="inherit"
              onClick={() => navigate('/support-requests')}
              sx={{ mr: 1 }}
            >
              <Badge badgeContent={supportOpenCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <div>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.name?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem disabled>
                <AccountCircle sx={{ mr: 1 }} />
                {user?.name}
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} />
                Sair
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      {/* Sub-bar de senhas do dia — sticky abaixo do AppBar, so em paginas relevantes */}
      {['/atendimento', '/monitor/clients'].includes(location.pathname) && (
        <Box sx={{
          position: 'fixed',
          top: 64,
          left: { xs: 0, sm: `${drawerWidth}px` },
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar - 1,
          bgcolor: 'rgba(25, 118, 210, 0.92)',
          borderBottom: '1px solid rgba(255,255,255,0.2)',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 0.75,
          backdropFilter: 'blur(4px)',
        }}>
          {liveStatus?.at && (() => {
            const ago = Math.max(0, Math.floor((Date.now() - new Date(liveStatus.at).getTime()) / 1000));
            const label = ago < 5 ? 'agora' : ago < 60 ? `${ago}s` : ago < 3600 ? `${Math.floor(ago / 60)}min` : `${Math.floor(ago / 3600)}h`;
            return (
              <Chip
                size="small"
                label={`● Live · ${label}`}
                sx={{
                  bgcolor: '#2e7d32',
                  color: '#fff',
                  fontWeight: 600,
                  fontFamily: 'monospace',
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            );
          })()}
          {(() => {
            // Senha do dia GetCard TEF — formula: ((dia + mes) * 4072259) + mes
            // dia/mes em horario de Brasilia (cliente sempre BRT).
            const brDate = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
            const dia = brDate.getDate();
            const mes = brDate.getMonth() + 1;
            const ano = brDate.getFullYear();
            // diaSemana Inovafarma: domingo=1, segunda=2, ..., sabado=7
            // (JS getDay() retorna domingo=0..sabado=6)
            const diaSemana = brDate.getDay() === 0 ? 1 : brDate.getDay() + 1;
            const getcard = ((dia + mes) * 4072259) + mes;
            const inovafarma = dia * mes * ano * diaSemana;
            return (
              <>
                {dailyPassword && (
                  <Tooltip title="Senha do dia do Tray (clique pra copiar). Vale ate meia-noite BRT — funciona em qualquer Tray 2.4.65+.">
                    <Chip
                      label={`Tray: ${dailyPassword}`}
                      size="small"
                      onClick={() => navigator.clipboard.writeText(dailyPassword)}
                      sx={{
                        mr: 1,
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        cursor: 'pointer',
                        bgcolor: '#ffd54f',
                        color: '#3e2723',
                        border: '1px solid rgba(255,255,255,0.6)',
                        '&:hover': { bgcolor: '#ffb300' },
                      }}
                    />
                  </Tooltip>
                )}
                <Tooltip title={`Senha do dia GetCard (TEF) — calculada localmente pra hoje (${dia}/${mes}). Click pra copiar.`}>
                  <Chip
                    label={`GetCard: ${getcard}`}
                    size="small"
                    onClick={() => navigator.clipboard.writeText(String(getcard))}
                    sx={{
                      mr: 1,
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      cursor: 'pointer',
                      bgcolor: '#ffffff',
                      color: '#0d47a1',
                      border: '1px solid rgba(255,255,255,0.6)',
                      '&:hover': { bgcolor: '#e3f2fd' },
                    }}
                  />
                </Tooltip>
                <Tooltip title={`Senha do dia Inovafarma — calculada localmente pra hoje (${dia}/${mes}/${ano}, dia da semana ${diaSemana}). Click pra copiar.`}>
                  <Chip
                    label={`Inovafarma: ${inovafarma}`}
                    size="small"
                    onClick={() => navigator.clipboard.writeText(String(inovafarma))}
                    sx={{
                      mr: 1,
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      cursor: 'pointer',
                      bgcolor: '#a5d6a7',
                      color: '#1b5e20',
                      border: '1px solid rgba(255,255,255,0.6)',
                      '&:hover': { bgcolor: '#81c784' },
                    }}
                  />
                </Tooltip>
              </>
            );
          })()}
        </Box>
      )}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_OPEN_WIDTH },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              overflowX: 'hidden',
              transition: (theme) => theme.transitions.create('width', { duration: theme.transitions.duration.shortest }),
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: ['/atendimento', '/monitor/clients'].includes(location.pathname) ? 11 : 8,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

export default Layout;
